import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const chapters = [
  {
    id: "chapter-land",
    num: "01",
    key: "land",
    title: "Land Acquisition",
    headline: "Ground truth.",
    copy: "Title-verified parcels in high-growth corridors, sourced off-market and diligenced to the last survey stone. We acquire land the way institutions do — patiently, and on paper.",
    img: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1600&q=85",
    alt: "Golden land parcel at sunset",
    stat: ["420+", "Acres placed with private clients"],
  },
  {
    id: "chapter-luxury",
    num: "02",
    key: "luxury-apartments",
    title: "Luxury Apartments",
    headline: "Live above the ordinary.",
    copy: "A tightly held portfolio of marquee residences — skyline penthouses, heritage conversions, and branded addresses. Each one walk-through ready, each one an appreciating asset.",
    img: "https://images.pexels.com/photos/9976125/pexels-photo-9976125.jpeg",
    alt: "Contemporary luxury living room",
    stat: ["96%", "Portfolio occupancy across residences"],
  },
  {
    id: "chapter-wealth",
    num: "03",
    key: "mutual-funds",
    title: "Wealth Management",
    headline: "Capital, compounding.",
    copy: "Goal-mapped mutual fund strategies across equity, debt and hybrid mandates — rebalanced quarterly, reviewed with you personally. Growth that answers to a plan, not a mood.",
    img: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&w=1600&q=85",
    alt: "Revenue growth chart on a laptop",
    stat: ["14.2%", "10-yr blended CAGR of model portfolios"],
  },
  {
    id: "chapter-insurance",
    num: "04",
    key: "insurance",
    title: "Insurance",
    headline: "Protection without fine print.",
    copy: "Term, health and asset cover structured around your balance sheet — not an agent's target. We negotiate group-grade terms for individuals, and stand beside you at claim time.",
    img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1600&q=85",
    alt: "Signing an insurance policy document",
    stat: ["₹850Cr+", "Cover under active advisory"],
  },
  {
    id: "chapter-commercial",
    num: "05",
    key: "commercial",
    title: "Commercial Property",
    headline: "Assets that earn while you sleep.",
    copy: "Grade-A offices, high-street retail and pre-leased warehousing — underwritten for yield, diligenced for title, and placed with tenants before you sign. Every asset is presented the way institutions read it: carpet area, rental yield, occupancy covenant and Cr/L pricing, with term sheets turned around in 72 hours.",
    img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjB3ZWFsdGglMjBtYW5hZ2VtZW50fGVufDB8fHx8MTc4OTM5NDIwOHww&ixlib=rb-4.1.0&q=85",
    alt: "Grade-A commercial towers",
    stat: ["6.8–8.1%", "Indicative yields on pre-leased assets"],
    link: "/commercialproperties",
    linkLabel: "Explore Commercial Inventory",
  },
];

const ChapterBlock = ({ chapter, index, onInterest }) => {
  const reversed = index % 2 === 1;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  return (
    <article
      ref={ref}
      id={chapter.id}
      data-testid={chapter.id}
      className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center py-24 md:py-32 border-t border-white/5"
    >
      <span className="pointer-events-none select-none absolute -top-4 lg:top-8 right-0 font-mono-lux text-[26vw] lg:text-[13rem] leading-none text-white/[0.035]" aria-hidden="true">
        {chapter.num}
      </span>

      <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className={`lg:col-span-7 chapter-image ${reversed ? "lg:order-2" : ""}`}
      >
        <div className="aspect-[4/3] w-full overflow-hidden">
          <img src={chapter.img} alt={chapter.alt} loading="lazy" className="w-full h-full object-cover" />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className={`lg:col-span-5 ${reversed ? "lg:order-1" : ""}`}
      >
        <p className="font-mono-lux text-[11px] uppercase tracking-[0.3em] text-[#C8A97E] mb-5">
          Chapter {chapter.num} — {chapter.title}
        </p>
        <div className="overflow-hidden">
          <motion.h3
            initial={{ y: "105%" }}
            animate={inView ? { y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl md:text-5xl lg:text-6xl text-[#F8F9FA] leading-[1.05] tracking-tight"
          >
            {chapter.headline}
          </motion.h3>
        </div>
        <p className="mt-6 text-sm md:text-base text-[#8A8D98] leading-relaxed max-w-md">{chapter.copy}</p>
        <div className="mt-8 flex items-baseline gap-4">
          <span className="font-display text-3xl md:text-4xl text-[#C8A97E]">{chapter.stat[0]}</span>
          <span className="font-mono-lux text-[10px] uppercase tracking-[0.2em] text-[#8A8D98] max-w-[180px]">{chapter.stat[1]}</span>
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-8">
          <button
            onClick={() => onInterest(chapter.key)}
            data-testid={`interest-button-${chapter.key}`}
            className="group inline-flex items-center gap-3 border-b border-[#C8A97E]/40 pb-2 text-[11px] font-mono-lux uppercase tracking-[0.25em] text-[#F8F9FA] hover:text-[#C8A97E] transition-colors duration-300"
          >
            Submit Interest
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
          {chapter.link && (
            <a
              href={chapter.link}
              data-testid={`chapter-link-${chapter.key}`}
              className="group inline-flex items-center gap-3 border-b border-[#C8A97E] pb-2 text-[11px] font-mono-lux uppercase tracking-[0.25em] text-[#C8A97E] hover:text-[#F8F9FA] transition-colors duration-300"
            >
              {chapter.linkLabel}
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          )}
        </div>
      </motion.div>
    </article>
  );
};

export const Chapters = ({ onInterest }) => (
  <section className="max-w-[1600px] mx-auto px-6 md:px-12" data-testid="chapters-section">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9 }}
      className="pt-24 md:pt-32 pb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
    >
      <h2 className="font-display text-4xl md:text-5xl text-[#F8F9FA] tracking-tight">
        The <span className="italic text-[#C8A97E]">Manifesto</span>
      </h2>
      <p className="font-mono-lux text-[11px] uppercase tracking-[0.3em] text-[#8A8D98]">Four disciplines. One desk.</p>
    </motion.div>
    {chapters.map((c, i) => (
      <ChapterBlock key={c.key} chapter={c} index={i} onInterest={onInterest} />
    ))}
  </section>
);
